import { setupAuthentication } from './auth.js';
import { fetchCardData } from './data/update_operations.js';
import { getUserToken } from './auth_utils.js';
import config from '../config/index.js';
import './navbar.js';

/************************************************
 * HTML Element IDs
 ************************************************/
const summariesContainerId = 'inbox-summaries'; // The main inbox list
const modalBackdropId = 'conversation-modal-backdrop'; // The conversation modal overlay
const modalCloseId = 'conversation-modal-close'; // The “×” close button
const modalTitleId = 'conversation-modal-title'; // Title area in the modal
const modalMessagesId = 'conversation-modal-messages'; // Container for the message items

/************************************************
 * State
 ************************************************/
let currentUserId = null;
let allMessages = [];
let conversationMap = {};

document.addEventListener('DOMContentLoaded', () => {
  setupAuthentication(handleAuthenticatedUser, handleUnauthenticatedUser);
});

/************************************************
 * handleAuthenticatedUser / handleUnauthenticatedUser
 ************************************************/
async function handleAuthenticatedUser(firebaseUser, token) {
  await loadInboxMessages(token);
}

function handleUnauthenticatedUser() {
  const container = document.getElementById(summariesContainerId);
  if (container) {
    container.innerHTML = `<p>Please <a href="/login.html">log in</a> to view your inbox.</p>`;
  }
}

/************************************************
 * 1) Load All Messages => Summaries
 ************************************************/
async function loadInboxMessages(token) {
  const container = document.getElementById(summariesContainerId);
  if (!container) return;

  container.innerHTML = '<p>Loading your messages...</p>';

  try {
    const headers = { Authorization: `Bearer ${token}` };
    const res = await fetchCardData(
      `${config.detectionUrl}/inbox`,
      'GET',
      headers
    );

    if (!res.ok) {
      const errorData = await res.json();
      container.innerHTML = `<p>Error: ${errorData.error || 'Could not load inbox.'}</p>`;
      return;
    }

    const data = res.body;
    currentUserId = data.requesting_user_id || null;
    allMessages = data.messages || [];

    if (allMessages.length === 0) {
      container.innerHTML = '<p>No messages in your inbox.</p>';
      return;
    }

    // Group by conversation
    conversationMap = groupMessagesIntoConversations(allMessages);

    // Render the summaries
    renderConversationSummaries(conversationMap);
  } catch (err) {
    console.error('Error loading inbox messages:', err);
    container.innerHTML = '<p>An error occurred. Check console.</p>';
  }
}

/************************************************
 * 2) Group messages by conversation key
 ************************************************/
function groupMessagesIntoConversations(messages) {
  const map = {};

  messages.forEach((msg) => {
    const sortedPair = [msg.from_user_id, msg.to_user_id]
      .sort((a, b) => a - b)
      .join('-');
    const key = `${msg.user_card_id}::${msg.collection_id}::${sortedPair}`;

    if (!map[key]) {
      map[key] = {
        user_card_id: msg.user_card_id,
        collection_id: msg.collection_id,
        messages: [],
      };
    }
    map[key].messages.push(msg);
  });

  // Sort each conversation by created_at
  for (let k in map) {
    map[k].messages.sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );
  }

  return map;
}

/************************************************
 * 3) Render conversation summaries
 ************************************************/

function getCardTitle(reference_card) {
  let title;

  title = `${reference_card.year} ${reference_card.program} ${reference_card.release_set} ${reference_card.players}`;

  if (reference_card.parallel) {
    title += ': ' + reference_card.parallel;
  }

  return title;
}

function renderConversationSummaries(convMap) {
  const container = document.getElementById(summariesContainerId);
  if (!container) return;

  container.innerHTML = '';

  const keys = Object.keys(convMap);
  if (keys.length === 0) {
    container.innerHTML = '<p>No messages in your inbox.</p>';
    return;
  }

  keys.forEach((key) => {
    const conv = convMap[key];
    const msgs = conv.messages;
    // Last message snippet
    const lastMsg = msgs[msgs.length - 1];

    // Determine if any unread
    const anyUnread = msgs.some(
      (m) => !m.is_read && m.to_user_id === currentUserId
    );

    // Build the conversation row
    const rowDiv = document.createElement('div');
    rowDiv.className = 'conversation-row';
    if (anyUnread) {
      rowDiv.classList.add('unread-conversation');
    }

    const firstMsg = msgs[0];
    const photo_url = getOtherUserProfilePicUrl(msgs);

    if (photo_url) {
      // Example logic: if from_user_id == me, the "other" is to_user_id’s pic
      const userIconImg = document.createElement('img');
      userIconImg.src = photo_url;
      userIconImg.alt = 'User Icon';
      userIconImg.className = 'summary-user-icon';
      rowDiv.appendChild(userIconImg);
    }

    // === (B) Create a container for the text details (title, snippet) ===
    const detailsDiv = document.createElement('div');
    detailsDiv.className = 'conversation-details';

    // Title
    const titleEl = document.createElement('div');
    titleEl.className = 'conversation-title';
    titleEl.textContent = `${getOtherUsername(conv)} - ${getCardTitle(firstMsg.reference_card)}`;

    // Snippet
    const snippetEl = document.createElement('div');
    snippetEl.className = 'last-message-excerpt';
    const bodyText = lastMsg.message_body || '';
    snippetEl.textContent =
      bodyText.length > 50 ? `${bodyText.substring(0, 50)}...` : bodyText;

    detailsDiv.appendChild(titleEl);
    detailsDiv.appendChild(snippetEl);
    rowDiv.appendChild(detailsDiv);

    // === (C) “View” button or row listener ===
    const viewBtn = document.createElement('button');
    viewBtn.className = 'btn-view';
    viewBtn.textContent = 'View';
    viewBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openConversationModal(key);
    });
    rowDiv.appendChild(viewBtn);

    // Or make entire row clickable
    rowDiv.addEventListener('click', () => openConversationModal(key));

    // Add the row to the container
    container.appendChild(rowDiv);
  });
}

/************************************************
 * 4) Open conversation in a MODAL
 ************************************************/
function openConversationModal(conversationKey) {
  const modalBackdrop = document.getElementById('conversation-modal-backdrop');
  const modalTitleEl = document.getElementById(modalTitleId);
  const modalMessagesEl = document.getElementById(modalMessagesId);
  const cardWrapperEl = document.getElementById('card-photo-wrapper');

  if (!modalBackdrop || !modalTitleEl || !modalMessagesEl || !cardWrapperEl)
    return;

  // Show the modal
  modalBackdrop.style.display = 'flex';

  // Clear old content
  modalTitleEl.textContent = '';
  modalMessagesEl.innerHTML = '';
  cardWrapperEl.innerHTML = '';

  // Grab conversation
  const conv = conversationMap[conversationKey];
  if (!conv) {
    modalMessagesEl.innerHTML = '<p>Conversation not found.</p>';
    return;
  }

  // Insert card image if desired (no user icon overlay now)
  const firstMsg =
    conv.messages.find((m) => m.card_image_url) || conv.messages[0];

  modalTitleEl.textContent = `${firstMsg.from_username} - ${getCardTitle(firstMsg.reference_card)}`;

  if (firstMsg && firstMsg.card_image_url) {
    const cardImg = document.createElement('img');
    cardImg.src = firstMsg.card_image_url;
    cardImg.alt = 'Card Thumbnail';
    cardImg.className = 'message-card-image';
    cardWrapperEl.appendChild(cardImg);
  } else {
    cardWrapperEl.innerHTML = '<p>No card image available.</p>';
  }

  // Then render the grouped conversation
  renderConversationMessages(conv);

  // Mark read, set up send...
  markConversationAsRead(conversationKey);
  setupConversationSend(conversationKey);
  document.body.classList.add('no-scroll');
}

function setupConversationSend(conversationKey) {
  const sendButton = document.getElementById('conversation-send-button');
  const textArea = document.getElementById('conversation-input-text');
  if (!sendButton || !textArea) return;

  textArea.value = '';

  // Remove existing listener using removeEventListener
  sendButton.removeEventListener('click', handleSend);

  // Then define the function
  async function handleSend() {
    const messageBody = textArea.value.trim();
    if (!messageBody) {
      alert('Please enter a message.');
      return;
    }

    const conv = conversationMap[conversationKey];
    if (!conv) {
      alert('Conversation not found.');
      return;
    }

    const otherUserId = getOtherUserId(conv);
    const token = getUserToken();

    const payload = {
      to_user_id: otherUserId,
      card_id: conv.user_card_id,
      collection_id: conv.collection_id,
      message: messageBody,
    };

    try {
      const res = await fetch(`${config.detectionUrl}/post-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json();
        alert('Error sending message: ' + (errData.error || 'Unknown error.'));
        return;
      }

      // Insert into conv.messages
      const newMsg = {
        message_id: Number.MAX_SAFE_INTEGER,
        from_user_id: currentUserId,
        to_user_id: otherUserId,
        message_body: messageBody,
        card_image_url: null,
        from_username: getMyUsername(conv),
        created_at: fixTimestampForNewest(conv.messages),
        is_read: false,
      };

      conv.messages.push(newMsg);
      renderConversationMessages(conv);
      textArea.value = '';
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Error sending message. Check console.');
    }
  }

  // Now add it once
  sendButton.addEventListener('click', handleSend);
}

/************************************************

/************************************************
 * 5) Mark as read
 ************************************************/
async function markConversationAsRead(conversationKey) {
  const token = getUserToken();
  if (!token) return;

  const conv = conversationMap[conversationKey];
  if (!conv) return;

  const unreadIds = conv.messages
    .filter((m) => !m.is_read && m.to_user_id === currentUserId)
    .map((m) => m.message_id);

  if (unreadIds.length === 0) return;

  try {
    await fetch(`${config.detectionUrl}/mark-read`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message_ids: unreadIds }),
    });

    // Update local data
    unreadIds.forEach((id) => {
      const msg = conv.messages.find((m) => m.message_id === id);
      if (msg) msg.is_read = true;
    });

    // Re-render the summary to remove highlight
    renderConversationSummaries(conversationMap);
    updateGlobalInboxDot();
  } catch (err) {
    console.error('Error marking conversation read:', err);
  }
}

function renderConversationMessages(conv) {
  const modalMessagesEl = document.getElementById(modalMessagesId);
  if (!modalMessagesEl) return;

  // Clear old
  modalMessagesEl.innerHTML = '';

  // Sort & group
  conv.messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const groups = buildMessageGroups(conv.messages); // as shown above

  groups.forEach((group) => {
    // Create a "message-group" wrapper
    const groupDiv = document.createElement('div');
    groupDiv.className = 'message-group';

    // “mine” vs. “theirs” bubble logic (optional)
    if (group.from_user_id === currentUserId) {
      groupDiv.classList.add('my-group');
    } else {
      groupDiv.classList.add('their-group');
    }

    // 1) The group header
    const header = document.createElement('div');
    header.className = 'message-group-header';

    // (A) If we have a profile pic, add a small <img>
    if (group.user_profile_pic_url) {
      const icon = document.createElement('img');
      icon.src = group.user_profile_pic_url;
      icon.alt = 'User Profile';
      icon.className = 'user-chat-icon'; // define in CSS
      header.appendChild(icon);
    }

    // (B) Then the user’s name
    const nameSpan = document.createElement('span');
    nameSpan.textContent = group.username;
    header.appendChild(nameSpan);

    groupDiv.appendChild(header);

    // 2) The bubble containing all lines in this group
    const bubbleDiv = document.createElement('div');
    bubbleDiv.className = 'message-group-bubble';

    group.messages.forEach((m) => {
      const lineDiv = document.createElement('div');
      lineDiv.className = 'message-line';
      lineDiv.textContent = m.message_body;
      bubbleDiv.appendChild(lineDiv);
    });

    groupDiv.appendChild(bubbleDiv);

    // Append this entire group to the container
    modalMessagesEl.appendChild(groupDiv);
  });

  // Scroll to the bottom
  modalMessagesEl.scrollTop = modalMessagesEl.scrollHeight;
}

// The function to ensure a bigger date
function fixTimestampForNewest(existingMessages) {
  // find the last message's time
  if (!existingMessages.length) {
    return new Date().toISOString();
  }
  const lastCreated = existingMessages[existingMessages.length - 1].created_at;
  const lastTime = new Date(lastCreated).getTime();
  let localTime = Date.now();

  if (localTime <= lastTime) {
    // bump it 1 second ahead
    localTime = lastTime + 1000;
  }
  return new Date(localTime).toISOString();
}

function buildMessageGroups(sortedMessages) {
  const groups = [];
  let currentGroup = null;
  let lastSenderId = null;

  sortedMessages.forEach((msg) => {
    // If the sender changed, start a new group
    if (msg.from_user_id !== lastSenderId) {
      // close the old group if it exists
      if (currentGroup) {
        groups.push(currentGroup);
      }
      // start new group
      currentGroup = {
        from_user_id: msg.from_user_id,
        username: msg.from_username || `User #${msg.from_user_id}`,
        user_profile_pic_url: msg.from_user_profile_pic_url || null, // or however you store it
        messages: [],
      };
      lastSenderId = msg.from_user_id;
    }
    // add this message to current group's array
    currentGroup.messages.push({
      message_body: msg.message_body,
      created_at: msg.created_at,
    });
  });

  // push the final group
  if (currentGroup) {
    groups.push(currentGroup);
  }
  return groups;
}

function updateGlobalInboxDot() {
  // Loop through all conversations to see if any message is unread
  let anyUnread = false;
  Object.values(conversationMap).forEach((conv) => {
    if (
      conv.messages.some((m) => !m.is_read && m.to_user_id === currentUserId)
    ) {
      anyUnread = true;
    }
  });

  const dotEl = document.getElementById('inbox-unread-dot');
  if (dotEl) {
    dotEl.style.display = anyUnread ? 'block' : 'none';
  }
}

function getOtherUserProfilePicUrl(messages) {
  // Iterate through messages in the conversation

  const firstMsg = messages[0];

  if (!firstMsg) return null;

  if (firstMsg.from_user_id === currentUserId) {
    return firstMsg.to_user_profile_pic_url;
  }

  return firstMsg.from_user_profile_pic_url;
}

function getOtherUserId(conv) {
  // Example: if the first message has from_user_id != currentUserId,
  // that's the other user. Otherwise it's to_user_id.
  const firstMsg = conv.messages[0];
  if (!firstMsg) return null;

  if (firstMsg.from_user_id === currentUserId) {
    return firstMsg.to_user_id;
  }
  return firstMsg.from_user_id;
}

function getMyUsername(conv) {
  // Iterate through messages in the conversation
  for (const msg of conv.messages) {
    // Check if the current user is the sender
    if (msg.from_user_id === currentUserId) {
      // Return the username of the recipient
      return msg.from_username;
    }

    // Otherwise, if the current user is the recipient
    if (msg.to_user_id === currentUserId) {
      // Return the username of the sender
      return msg.to_username;
    }
  }
  return null;
}

function getOtherUsername(conv) {
  // Iterate through messages in the conversation
  for (const msg of conv.messages) {
    // Check if the current user is the sender
    if (msg.from_user_id === currentUserId) {
      // Return the username of the recipient
      return msg.to_username;
    }

    // Otherwise, if the current user is the recipient
    if (msg.to_user_id === currentUserId) {
      // Return the username of the sender
      return msg.from_username;
    }
  }
  return null;
}

/************************************************
 * 6) Close the Conversation Modal
 ************************************************/
document.getElementById(modalCloseId)?.addEventListener('click', () => {
  const modalBackdrop = document.getElementById(modalBackdropId);
  if (modalBackdrop) modalBackdrop.style.display = 'none';
  document.body.classList.remove('no-scroll');
});
